import React from 'react';
import { Slot, useRouter } from 'expo-router';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { KeyboardProvider } from 'react-native-keyboard-controller';
import { StatusBar } from 'expo-status-bar';
import { View, ActivityIndicator, Text, Platform, Pressable } from 'react-native';
import * as Linking from 'expo-linking';
import ErrorBoundary from '../src/components/ErrorBoundary';
import { FeedbackFab } from '@/components/feedback/FeedbackFab';
import PasswordlessMigrationBanner from '../src/components/auth/PasswordlessMigrationBanner';
import PaymentFailedBanner from '../src/components/billing/PaymentFailedBanner';
import WebSplashScreen from '../src/components/WebSplashScreen';
import '../global.css';
import { AuthContext, AuthPhase, BillingStatus, AuthUser, RefreshAuthOptions } from '../src/context/AuthContext';
import { TutorialProvider } from '../src/context/TutorialContext';
import { TipsProvider } from '../src/context/TipsContext';
import { CreditProvider } from '../src/context/CreditContext';
import { TrialProvider } from '../src/context/TrialContext';
import { WebSocketProvider } from '../src/context/WebSocketContext';
import { ToastProvider } from '../src/context/ToastContext';
import { TutorialLayer } from '../src/components/tutorial';
import { authAPI, billingAPI, invalidateBillingCaches, setOnUnauthorizedHandler } from '../src/services/api';
import { PostHogProvider } from '../src/providers/PostHogProvider';
import {
  captureUTMFromURL,
  identifyUser,
  registerBaseProperties,
  resetIdentity,
  trackAppInstallAttribution,
  trackAuthEvent,
  trackSubscriptionStatusChange,
} from '../src/services/analytics';
import {
  claimCheckoutCompletion,
  markCheckoutReturnConsumed,
  parseCheckoutReturn,
  readPendingCheckout,
  setCheckoutReturnMentorProfile,
  stripCheckoutReturnParams,
  type CheckoutReturn,
} from '../src/utils/checkoutCompletion';
import { clearPlanContext, initPlanContextFromURL } from '../src/utils/planContext';
import { takeSsoHandoffCode } from '../src/utils/ssoHandoff';
import { isMentorProfile } from '../src/utils/postCheckoutRoute';
import {
  isSuperAdmin as isSuperAdminCheck,
  isSchoolAdmin as isSchoolAdminCheck,
  isSchoolManagedUser,
} from '../src/utils/roleUtils';
import { ROUTES } from '../src/navigation/routes';
import { installStaleBuildRecovery } from '../src/utils/staleBuild';
import { phaseForAccessState } from '../src/navigation/gatedAccess';
import { ICFProgressAPI } from '../src/services/api/icfProgressAPI';
import { invalidateSidebarRecentDocumentsCache } from '../src/design-system/components';
import { initSentry, reportHandledError } from '../src/services/errorReporting';
import { installInAppHistory } from '../src/navigation/inAppHistory';
import { accountUnavailableEventProperties, isAccountUnavailable, loadAccountWithRetry } from '../src/utils/authLoad';
import { useTranslation } from '../src/hooks/useTranslation';

initSentry();
// Back buttons walk the browser history only within the app (inAppHistory.ts).
if (Platform.OS === 'web' && typeof window !== 'undefined') installInAppHistory(window);

function LoadingScreen() {
  return (
    <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8fafc' }}>
      <ActivityIndicator size="large" color="#2563eb" />
      <Text style={{ marginTop: 12, fontSize: 16, color: '#475569' }}>Loading…</Text>
    </View>
  );
}

/**
 * /auth/me kept failing for a transient reason (429, 5xx, network): say so and
 * offer a retry. The session is kept; this is never treated as a sign-out.
 */
function AccountUnavailableScreen({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <View testID="account_load_error" style={{ flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8fafc', padding: 24 }}>
      <Text accessibilityRole="header" style={{ fontSize: 18, fontWeight: '600', color: '#0f172a', textAlign: 'center' }}>
        {t('auth.accountLoadError.title')}
      </Text>
      <Text accessibilityRole="alert" style={{ marginTop: 8, fontSize: 15, color: '#475569', textAlign: 'center', maxWidth: 420 }}>
        {t('auth.accountLoadError.body')}
      </Text>
      <Pressable
        testID="account_load_retry"
        accessibilityRole="button"
        onPress={onRetry}
        style={{ marginTop: 20, paddingHorizontal: 20, paddingVertical: 10, borderRadius: 8, backgroundColor: '#2563eb' }}
      >
        <Text style={{ color: '#ffffff', fontSize: 15, fontWeight: '600' }}>{t('auth.accountLoadError.retry')}</Text>
      </Pressable>
    </View>
  );
}

export default function RootLayout() {
  const router = useRouter();
  const [phase, setPhase] = React.useState<AuthPhase>('loading');
  const [user, setUser] = React.useState<AuthUser | null>(null);
  const [billingStatus, setBillingStatus] = React.useState<BillingStatus | null>(null);
  // Set when /auth/me failed transiently after retries; phase stays 'loading'.
  const [accountUnavailable, setAccountUnavailable] = React.useState<{ retryAfterMs: number | null } | null>(null);
  const [showWebSplash, setShowWebSplash] = React.useState(
    Platform.OS === 'web' && process.env.NODE_ENV === 'production'
  );
  const refreshAuthInFlightRef = React.useRef<{
    promise: Promise<void>;
    forceBillingRefresh: boolean;
    forceUserRefresh: boolean;
  } | null>(null);
  const authenticatedUserIdRef = React.useRef<number | string | null>(null);
  const isLoading = phase === 'loading';

  const clearUserScopedCaches = React.useCallback(() => {
    ICFProgressAPI.invalidateDashboardCache();
    invalidateSidebarRecentDocumentsCache();
  }, []);

  // A tab opened before a deploy reloads onto the new build when a lazily loaded chunk is gone.
  React.useEffect(() => (Platform.OS === 'web' ? installStaleBuildRecovery() : undefined), []);

  React.useEffect(() => {
    const shouldLoadReactGrab =
      Platform.OS === 'web' &&
      process.env.EXPO_PUBLIC_ENABLE_REACT_GRAB === 'true';

    if (!shouldLoadReactGrab) {
      return;
    }

    void import('react-grab').catch((error) => {
      console.warn('Failed to load React Grab:', error);
    });
  }, []);

  const refreshAuth = React.useCallback(async (options?: RefreshAuthOptions) => {
    const requestedForces = {
      forceBillingRefresh: Boolean(options?.forceBillingRefresh),
      forceUserRefresh: Boolean(options?.forceUserRefresh),
    };
    const activeRefresh = refreshAuthInFlightRef.current;
    const needsStrongerFollowUp = Boolean(
      activeRefresh
      && (
        (requestedForces.forceBillingRefresh && !activeRefresh.forceBillingRefresh)
        || (requestedForces.forceUserRefresh && !activeRefresh.forceUserRefresh)
      )
    );

    if (activeRefresh && !needsStrongerFollowUp) {
      return activeRefresh.promise;
    }

    const task = (async () => {
      // A stronger refresh must run after a weaker one instead of coalescing
      // into it. Waiting also prevents the weaker response from overwriting
      // the forced response after a reset or checkout completes.
      if (activeRefresh) {
        try {
          await activeRefresh.promise;
        } catch {}
      }

      setPhase((current) => (current === 'loading' ? current : 'loading'));
      setAccountUnavailable(null);
      try {
        // 429/5xx/network are retried (honouring Retry-After); only 401/403 sign out.
        const currentUser = await loadAccountWithRetry(
          () => authAPI.getCurrentUser({ force: options?.forceUserRefresh }),
        );
        const nextUserId = currentUser?.id ?? null;
        if (authenticatedUserIdRef.current !== nextUserId) {
          clearUserScopedCaches();
          authenticatedUserIdRef.current = nextUserId;
        }
        setUser(currentUser);

        // Use centralized roleUtils helpers — no inline user_type checks
        const u = currentUser as any;
        const isSuperAdmin = isSuperAdminCheck(u);
        const isSchoolAdmin = isSchoolAdminCheck(u);
        const isSchoolManaged = isSchoolManagedUser(u);
        const hasSchool = Boolean(currentUser?.school_id);

        try {
          try {
            await billingAPI.refresh({ force: options?.forceBillingRefresh });
          } catch (error) {
            // status() below still runs; a failed refresh can leave billing stale.
            reportHandledError(error, {
              source: 'billing.refresh',
              forced: Boolean(options?.forceBillingRefresh),
            });
          }
          const status = await billingAPI.status({ force: options?.forceBillingRefresh });
          setBillingStatus(status);
          void trackSubscriptionStatusChange(status);

          setPhase(phaseForAccessState(currentUser, status));
        } catch {
          setBillingStatus(null);
          if (isSchoolAdmin && hasSchool) {
            setPhase('b2b_plan_selection');
            return;
          }
          if (isSchoolManaged) {
            setPhase('school_inactive');
            return;
          }
          if (!isSuperAdmin) {
            setPhase('billing_recovery');
            return;
          }
          setPhase('authenticated');
        }
      } catch (error) {
        if (isAccountUnavailable(error)) {
          // Keep the session and the user; show the retry state instead of a
          // blank page or the login screen.
          setAccountUnavailable({ retryAfterMs: error.retryAfterMs });
          trackAuthEvent('account_load_retry_shown', accountUnavailableEventProperties(error));
          return;
        }
        if (authenticatedUserIdRef.current !== null) {
          clearUserScopedCaches();
          authenticatedUserIdRef.current = null;
        }
        setUser(null);
        setBillingStatus(null);
        setPhase('unauthenticated');
      }
    })();

    refreshAuthInFlightRef.current = {
      promise: task,
      ...requestedForces,
    };
    try {
      await task;
    } finally {
      if (refreshAuthInFlightRef.current?.promise === task) {
        refreshAuthInFlightRef.current = null;
      }
    }
  }, [clearUserScopedCaches]);

  const retryAccountLoad = React.useCallback(() => {
    void refreshAuth({ forceUserRefresh: true });
  }, [refreshAuth]);

  // Rate-limited: try again on our own once the server's Retry-After passes.
  React.useEffect(() => {
    const waitMs = accountUnavailable?.retryAfterMs;
    if (waitMs == null) return undefined;
    const timer = setTimeout(retryAccountLoad, waitMs);
    return () => clearTimeout(timer);
  }, [accountUnavailable, retryAccountLoad]);

  const retryAccountLoadClicked = React.useCallback(() => {
    trackAuthEvent('account_load_retry_clicked');
    retryAccountLoad();
  }, [retryAccountLoad]);

  const logout = React.useCallback(async () => {
    try {
      await authAPI.logout();
    } catch {}
    await resetIdentity();
    clearUserScopedCaches();
    authenticatedUserIdRef.current = null;
    setUser(null);
    setBillingStatus(null);
    setPhase('unauthenticated');
    router.replace(ROUTES.PUBLIC_LOGIN as any);
  }, [clearUserScopedCaches, router]);

  React.useEffect(() => {
    registerBaseProperties();
    captureUTMFromURL(typeof window !== 'undefined' ? window.location.href : undefined);
    // Landing pricing cards link to /signup?plan=tierN&cycle=...; keep the
    // choice before any phase redirect drops the query (plan selection
    // preselects it after sign-in and intake).
    initPlanContextFromURL('landing');
    void trackAppInstallAttribution();

    const subscription = Linking.addEventListener('url', (event) => {
      captureUTMFromURL(event.url);
    });

    return () => {
      subscription?.remove?.();
    };
  }, []);

  React.useEffect(() => {
    if (user?.id) {
      identifyUser(user);
    }
  }, [user]);

  React.useEffect(() => {
    let checkoutReturn: CheckoutReturn | null = null;
    const run = async () => {
      try {
        if (typeof window !== 'undefined') {
          // Stripe embedded checkout sent the whole tab back to return_url
          // (redirect-based payment methods). Consume the params before the
          // phase redirect drops them, then complete below.
          checkoutReturn = parseCheckoutReturn(window.location.href, {
            isPopup: Boolean(window.opener),
          });
          if (checkoutReturn) {
            markCheckoutReturnConsumed();
            window.history.replaceState({}, document.title, stripCheckoutReturnParams(window.location.href));
          }

          // Landing "Open App" SSO handoff. The web PostHog provider already
          // stripped `code` from the address bar at import time, before
          // analytics could record the URL; pick it up from there.
          const code = takeSsoHandoffCode();
          if (code) {
            try {
              const raw = process.env.EXPO_PUBLIC_API_URL || 'http://localhost:8000';
              const hasScheme = /^https?:\/\//i.test(raw);
              const apiBase = hasScheme
                ? raw
                : window.location.protocol === 'https:'
                ? `https://${raw}`
                : `http://${raw}`;
              await fetch(`${apiBase}/api/auth/sso/exchange`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                // Important: include credentials so the browser accepts Set-Cookie from CORS response
                credentials: 'include',
                body: JSON.stringify({ code }),
              });
            } catch (error) {
              // Network failure: the user lands signed out with no other signal.
              reportHandledError(error, { source: 'auth.sso_exchange' });
            }
          }

          const url = new URL(window.location.href);
          const from = url.searchParams.get('from');
          if (from === 'landing') {
            url.searchParams.delete('from');
            window.history.replaceState({}, document.title, url.toString());
          }
        }
      } finally {
        if (checkoutReturn) {
          void completeCheckoutReturn(checkoutReturn);
        } else {
          refreshAuth();
        }
      }
    };

    // Same steps as the in-app onComplete handler in PlanSelectionScreen:
    // track once, then a forced billing refresh moves the phase forward and
    // the layouts route by phase.
    const completeCheckoutReturn = async (landing: CheckoutReturn) => {
      const pending = readPendingCheckout();
      const sessionId = landing.sessionId || null;
      if (claimCheckoutCompletion(sessionId)) {
        trackAuthEvent('checkout_completed', {
          plan: pending?.session_id === sessionId || !sessionId ? pending?.plan : undefined,
          billing_period: pending?.session_id === sessionId || !sessionId ? pending?.billing_period : undefined,
          completion_source: 'stripe_return_url',
          checkout_session_id: sessionId || undefined,
        });
      }
      clearPlanContext();
      invalidateBillingCaches();
      // Read the intake before the phase resolves so the public layout's
      // first redirect can send a tier3 mentor to Mentees.
      try {
        setCheckoutReturnMentorProfile(isMentorProfile(await authAPI.getIntakeSummary()));
      } catch {
        setCheckoutReturnMentorProfile(false);
      }
      await refreshAuth({ forceBillingRefresh: true });
    };

    run();

    setOnUnauthorizedHandler(() => {
      // Mirror logout(): a forced/expired-session logout must also detach the
      // PostHog identity, otherwise the next (possibly different) user inherits
      // the previous person's analytics identity.
      void resetIdentity();
      clearUserScopedCaches();
      authenticatedUserIdRef.current = null;
      setPhase('unauthenticated');
      setUser(null);
      setBillingStatus(null);
      router.replace(ROUTES.PUBLIC_LOGIN as any);
    });

    return () => {
      setOnUnauthorizedHandler(null);
    };
  }, [clearUserScopedCaches, refreshAuth, router]);

  const contextValue = React.useMemo(
    () => ({
      phase,
      user,
      billingStatus,
      isLoading,
      refreshAuth,
      logout,
      setPhase,
      setBillingStatus,
      setUser,
    }),
    [phase, user, billingStatus, isLoading, refreshAuth, logout]
  );

  // Show web splash screen on first load (web only). It hides as soon as auth
  // resolves (phase leaves 'loading') past a short minimum, so a logged-in user
  // is not made to wait on a fixed timer.
  if (showWebSplash) {
    return (
      <WebSplashScreen
        ready={phase !== 'loading' || accountUnavailable !== null}
        onFinish={() => setShowWebSplash(false)}
      />
    );
  }

  // The app-level boundary is outermost so a crash in the PostHog/Keyboard
  // providers does not white-screen; screens have their own boundaries
  // (WebAppShell content, route-level ErrorBoundary exports) (FE-22).
  return (
    <ErrorBoundary scope="app">
      <PostHogProvider>
        <GestureHandlerRootView style={{ flex: 1 }}>
          <KeyboardProvider preload={false}>
            <AuthContext.Provider value={contextValue}>
              <WebSocketProvider disabled={phase !== 'authenticated'} userKey={user?.id ?? null}>
                <ToastProvider>
                  <TipsProvider>
                    <CreditProvider>
                      <TrialProvider>
                        <TutorialProvider>
                          {isLoading ? (
                            accountUnavailable ? <AccountUnavailableScreen onRetry={retryAccountLoadClicked} /> : <LoadingScreen />
                          ) : (
                            <View style={{ flex: 1 }} pointerEvents="box-none">
                              {/* Legacy-user notice: only renders when the
                                  authenticated user has not yet acknowledged
                                  the passwordless migration. Self-hides on
                                  new accounts and on logout. */}
                              {phase === 'authenticated' && <PasswordlessMigrationBanner />}
                              {/* D10: past_due grace; self-hides otherwise. */}
                              {phase === 'authenticated' && <PaymentFailedBanner />}
                              <Slot />
                              <FeedbackFab />
                              {/* Tutorial UI layer - rendered above everything */}
                              <TutorialLayer />
                            </View>
                          )}
                        </TutorialProvider>
                      </TrialProvider>
                    </CreditProvider>
                  </TipsProvider>
                </ToastProvider>
              </WebSocketProvider>
            </AuthContext.Provider>
          </KeyboardProvider>
          <StatusBar style="auto" />
        </GestureHandlerRootView>
      </PostHogProvider>
    </ErrorBoundary>
  );
}

export const unstable_settings = {
  initialRouteName: '(public)',
};
